import EmergencyPlanningIcon from '@app/domains/useCases/components/EmergencyPlanningIcon'
import type { UseCaseConfig } from '../types'

export const emergencyPlanningAndBusinessContinuity: UseCaseConfig = {
  id: 'emergency-planning-and-business-continuity',
  title: 'Emergency Planning and Business Continuity',
  tagline: 'The benefit of hindsight, beforehand.',
  accentColor: 'dustyRose',
  iconComponent: EmergencyPlanningIcon,
  roles: [
    'CDEM officers and emergency management coordinators',
    'Emergency managers and group controllers',
    'Lifeline utility and critical infrastructure operators',
    'Business continuity leads',
    'Emergency management advisors in government',
    'Emergency management consultancies',
  ],
  keyQuestions: [
    'What hazard scenarios are we planning for, and how credible are our assumptions?',
    'Which assets and services are most likely to fail first, and what cascades would that trigger?',
    'Which communities are most exposed and least able to self-manage, and where will demand for evacuation, welfare, and emergency support concentrate?',
    'What minimum service levels must be maintained, and where should resources be pre-positioned to meet them?',
  ],
  relatedUseCaseIds: ['adaptation-planning', 'asset-and-infrastructure-planning'],
  forumUrl: 'https://forum.resilience-explorer.com/c/emergency',
  overviewBody:
    'Emergency and continuity planning requires preparing for events that have not yet occurred, in systems that are more interconnected than many plans assume. Plans built on generic hazard scenarios rather than credible, location-specific evidence cannot be validated in advance: teams do not know which assets are likely to fail first, evacuation routes go untested against actual network conditions, and cascade effects across lifeline utilities are unexamined until they occur under pressure. The gap between plan and reality is often only discovered during an event, when there is little time to close it.',
  framingQuestion:
    'How do we ensure our plans reflect what will actually happen during a hazard event?',
  learningPathwayIntro:
    'Emergency and continuity planning requires translating hazard scenarios into credible, location-specific evidence about what will fail, where demand will concentrate, and which communities are most exposed. The articles below are organised to support that, from risk fundamentals through to scenario development and community vulnerability.',
  tutorials: [
    {
      type: 'TUTORIAL',
      date: '09/10/25',
      title: 'Workspace Overview',
      description:
        'Learn how to organise hazard data, saved views, and workflows using Workspaces in Resilience Explorer.',
      thumbnail: '',
      href: '/app/guide/tutorials/organise-with-workspaces/workspaces-overview',
    },
    {
      type: 'DEMO',
      date: '09/10/25',
      title: 'Assessing risk to electricity transmission assets',
      description:
        'Explore how groundwater hazards can impact electricity transmission infrastructure using a real-world risk assessment workflow.',
      thumbnail: '',
      href: '/app/guide/demos/assessing-risk-to-assets/assessing-risk-to-electricity-transmission-assets-groundwater-case-study',
    },
    {
      type: 'TUTORIAL',
      date: '09/10/25',
      title: 'Change how layers are displayed on the map',
      description:
        'Understand how to customise map layers and visualise hazard information more effectively during planning and response.',
      thumbnail: '',
      href: '/app/guide/tutorials/displaying-information-on-the-map/change-how-layers-are-displayed-on-the-map',
    },
    {
      type: 'TUTORIAL',
      date: '09/10/25',
      title: 'Filter results to an area of your choice',
      description:
        'Learn how to focus analysis on specific locations and operational areas using spatial filtering tools.',
      thumbnail: '',
      href: '/app/guide/tutorials/download-export-and-share-results/filter-results-to-an-area-of-your-choice',
    },
    {
      type: 'TUTORIAL',
      date: '09/10/25',
      title: 'Copy map to other documents',
      description:
        'Export and reuse map outputs in reports, presentations, and operational planning documents.',
      thumbnail: '',
      href: '/app/guide/tutorials/download-export-and-share-results/copy-map-to-other-documents',
    },
    {
      type: 'TUTORIAL',
      date: '09/10/25',
      title: 'Download an element report',
      description:
        'Generate downloadable reports for selected assets, infrastructure, and exposed elements.',
      thumbnail: '',
      href: '/app/guide/tutorials/download-export-and-share-results/download-an-element-report',
    },
  ],
  articleGroups: [
    {
      title: 'Risk fundamentals for emergency planners',
      articles: [
        {
          title: 'What is risk',
          description:
            'The two-part definition applied to emergency planning: consequences and uncertainty, and why both determine how credible a scenario plan is.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-risk',
        },
        {
          title: 'What are consequences',
          description:
            'Direct and indirect consequences: why asset failure rarely captures the full human and service impact of an event.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-are-consequences',
        },
        {
          title: 'Criticality',
          description:
            'How to determine which assets matter most in a network, and why the most critical assets are not always the most visibly important ones.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/what-is-criticality',
        },
        {
          title: 'Cascading Hazards',
          description:
            'How hazard events trigger secondary failures across systems: the pattern that emergency plans must anticipate.',
          href: '/app/guide/fundamentals/hazards-and-stressors/cascading-hazards',
        },
        {
          title: 'Foundations of Risk Communication',
          description:
            'The principles underpinning effective risk communication to communities, partner agencies, and the public: how to communicate scenarios and warnings in ways that inform rather than overwhelm.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/foundations-of-risk-communication',
        },
        {
          title: 'Why Numbers Matter in Hazard Communication',
          description:
            'How to communicate event probabilities and scenario likelihoods in ways that motivate appropriate preparedness without producing either complacency or alarm.',
          href: '/app/guide/insights/research-and-methods/why-numbers-matter-in-hazard-communication',
        },
        {
          title: 'Foundations of Risk Perception',
          description:
            'What shapes how communities and decision-makers perceive emergency risk, and why people with identical information can reach very different conclusions about preparedness and urgency.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/foundations-of-risk-perception',
        },
      ],
    },
    {
      title: 'Infrastructure dependencies and cascades',
      articles: [
        {
          title: 'Guidance on adaptive decision making for complex hazards',
          description:
            'Structuring decisions for systems with multiple interdependencies, including under time pressure and incomplete information.',
          href: '/app/guide/insights/research-and-methods/from-planning-to-action-a-practical-guide-for-climate-ready-infrastructure',
        },
      ],
    },
    {
      title: 'Community exposure and welfare demand',
      articles: [
        {
          title: 'Social Vulnerability',
          description:
            'How social vulnerability is defined and measured, and why it matters for prioritising emergency welfare and pre-positioning resources.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/social-vulnerability',
        },
        {
          title: 'Why Social Vulnerability Indicators Tell Only Half the Story',
          description:
            'The limitations of standard composite indices, and what a more complete picture of community capacity requires.',
          href: '/app/guide/insights/commentary-and-opinion/why-social-vulnerability-indicators-tell-only-half-the-story',
        },
        {
          title: 'Adaptive Capacity',
          description:
            'The economic, social, institutional, and environmental factors that determine how well communities and organisations can respond to and recover from events.',
          href: '/app/guide/fundamentals/adaptation-and-system-change/adaptive-capacity',
        },
        {
          title: 'Supporting Those Who Respond in Extreme Weather Events',
          description:
            'The capacity and wellbeing of emergency responders as a resource that plans must account for.',
          href: '/app/guide/insights/research-and-methods/supporting-those-who-respond-in-extreme-weather-events',
        },
      ],
    },
    {
      title: 'Scenario-based planning',
      articles: [
        {
          title: 'The Planning Process for Risk',
          description:
            'How to scope and set up a risk assessment that produces credible, decision-ready outputs: the foundation for defensible emergency scenario development.',
          href: '/app/guide/fundamentals/planning-and-implementation/the-planning-process-for-risk',
        },
        {
          title: 'Understanding Return Periods',
          description:
            'What return periods mean for defining credible event scenarios, and how they shift under changing climate conditions.',
          href: '/app/guide/fundamentals/hazards-and-stressors/understanding-return-periods-and-ae-ps',
        },
        {
          title: 'Types of Uncertainty',
          description:
            'The different types of uncertainty in hazard and consequence modelling, and what they mean for how confidently scenarios can be defined.',
          href: '/app/guide/fundamentals/risk-consequences-and-uncertainty/types-of-uncertainty',
        },
        {
          title: 'Strength of Knowledge',
          description:
            'How to characterise and communicate the quality of evidence underlying scenario assumptions.',
          href: '/app/guide/fundamentals/evidence-and-confidence/strength-of-knowledge',
        },
      ],
    },
  ],
  dataNeedsIntro:
    'The quality and completeness of available data determines how realistically these conditions can be represented. The table below shows the four levels of capability, from identifying exposed areas and assets through to testing realistic disruption and response scenarios, and the minimum datasets required to support each level.',
  capabilityLevels: [
    {
      level: 1,
      title: 'Identify exposure',
      description: 'Understand which areas, assets, and communities are exposed to hazards.',
    },
    {
      level: 2,
      title: 'Estimate impacts',
      description: 'Identify likely disruptions, affected services, and impacted populations.',
    },
    {
      level: 3,
      title: 'Plan response priorities',
      description:
        'Determine where response actions, resources, and welfare support are needed most.',
    },
    {
      level: 4,
      title: 'Test response scenarios',
      description: 'Validate plans against realistic, scenario-based disruption conditions.',
    },
  ],
  dataRequirements: [
    {
      dataset: 'Extent (e.g. flood extent, tsunami extent)',
      category: 'Hazard',
      startLevel: 1,
    },
    {
      dataset: 'Intensity (e.g. flood depth, velocity, wind speed)',
      category: 'Hazard',
      startLevel: 2,
    },
    {
      dataset: 'Scenarios (e.g. relevant return periods, event scenarios)',
      category: 'Hazard',
      startLevel: 3,
    },
    {
      dataset: 'Temporal profile (e.g. timing, duration)',
      category: 'Hazard',
      startLevel: 3,
    },
    {
      dataset: 'Asset locations (e.g. roads, bridges, substations, facilities)',
      category: 'Infrastructure and assets',
      startLevel: 1,
    },
    {
      dataset: 'Road network (e.g. road centerlines)',
      category: 'Infrastructure and assets',
      startLevel: 1,
    },
    {
      dataset: 'Critical facilities (e.g. schools, hospitals, aged care)',
      category: 'Infrastructure and assets',
      startLevel: 1,
    },
    {
      dataset: 'Lifeline networks (e.g. power, water, communications)',
      category: 'Infrastructure and assets',
      startLevel: 2,
    },
    {
      dataset: 'Asset attributes (e.g. type, criticality, condition)',
      category: 'Infrastructure and assets',
      startLevel: 2,
    },
    {
      dataset: 'Dependencies between assets and systems (e.g. power-water dependencies)',
      category: 'Infrastructure and assets',
      startLevel: 3,
    },
    {
      dataset: 'Welfare facilities and staging locations (e.g. evacuation centres, hubs)',
      category: 'Services and response',
      startLevel: 2,
    },
    {
      dataset: 'Evacuation zones and routes (e.g. evacuation areas, safe routes)',
      category: 'Services and response',
      startLevel: 2,
    },
    {
      dataset: 'Alerting infrastructure (e.g. sirens, alert zones, coverage areas)',
      category: 'Services and response',
      startLevel: 3,
    },
    {
      dataset: 'Response assets (e.g. depots, EOCs, equipment locations)',
      category: 'Services and response',
      startLevel: 3,
    },
    {
      dataset: 'Residential population (e.g. census population)',
      category: 'Population and demand',
      startLevel: 1,
    },
    {
      dataset: 'Workforce and activity data (e.g. employment, commercial areas)',
      category: 'Population and demand',
      startLevel: 2,
    },
    {
      dataset: 'Facility populations (e.g. schools, hospitals, aged care occupancy)',
      category: 'Population and demand',
      startLevel: 2,
    },
    {
      dataset: 'Vulnerability indicators (e.g. age, deprivation, mobility)',
      category: 'Population and demand',
      startLevel: 3,
    },
    {
      dataset: 'Time-of-day or seasonal variation (e.g. daytime population, tourism seasonality)',
      category: 'Population and demand',
      startLevel: 4,
    },
  ],
}
